/**
 * SoleadLab 2.0 - Project Selection Modal
 *
 * Shown after login. Lists existing projects, allows creating new ones,
 * and importing from file.
 */

import { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import Button from './Button';
import { listProjects, createProject, deleteProject, restoreProject, purgePreviewProject, purgeProject, getPurgeStatus, listClients, moveProject, listAdminOrganizations } from '../../services/projectApi';
import type { SiteListItem, ClientItem, PurgePreviewResult, AdminOrganizationItem } from '../../services/projectApi';
import { importProjectZip } from '../../services/projectImport';
import { useProjectStore } from '../../stores/projectStore';
import { useMapStore } from '../../stores/mapStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import './ProjectSelectModal.css';

interface ProjectSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// M-05D-3: approved purge reason codes (enum) + display labels. Mirrors the
// backend reasonCode enum; no raw free-text reason is ever sent.
const PURGE_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'customer_offboarding', label: 'Customer offboarding' },
  { value: 'legal_request', label: 'Legal / data-removal request' },
  { value: 'duplicate_project', label: 'Duplicate project' },
  { value: 'test_data', label: 'Test data' },
  { value: 'admin_cleanup', label: 'Admin cleanup' },
  { value: 'other', label: 'Other approved reason' },
];

export default function ProjectSelectModal({ isOpen, onClose }: ProjectSelectModalProps) {
  const [projects, setProjects] = useState<SiteListItem[]>([]);
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New project form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [creating, setCreating] = useState(false);

  // Project-delete confirmation (M-05A). Delete is destructive + irreversible
  // under current backend behavior, so it is platform-admin-only (matching the
  // backend handleDeleteSite admin gate) and requires typing the exact name.
  const [deleteTarget, setDeleteTarget] = useState<SiteListItem | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // M-05C: platform-admin-only "Show archived" filter. Default OFF.
  const [showArchived, setShowArchived] = useState(false);

  // M-05D-3: physical-purge (true erasure) flow — admin-only, archived rows only.
  // Distinct from Restore; IRREVERSIBLE. Starts with a counts-only preview.
  const [purgeTarget, setPurgeTarget] = useState<SiteListItem | null>(null);
  const [purgePreview, setPurgePreview] = useState<PurgePreviewResult | null>(null);
  const [purgePreviewLoading, setPurgePreviewLoading] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeReasonCode, setPurgeReasonCode] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeStatus, setPurgeStatus] = useState<string | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  // Move-to-organization (platform admin, active rows only). Typed-name
  // confirmation like purge; the server re-checks everything (admin role,
  // archived, name, target org). Organizations load once per modal open.
  const [moveTarget, setMoveTarget] = useState<SiteListItem | null>(null);
  const [moveOrgId, setMoveOrgId] = useState('');
  const [moveConfirmText, setMoveConfirmText] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [adminOrgs, setAdminOrgs] = useState<AdminOrganizationItem[] | null>(null);

  const { addNotification, showConfirm } = useUIStore();
  const { loadProject, loadProjectFromServer } = useProjectStore();
  // Platform-admin = the global user role (NOT the per-org role). This mirrors
  // EditorPage/AdminPage and the backend handleDeleteSite `ctx.role === 'admin'`
  // gate; org owners/operators intentionally do NOT get project-delete UI.
  const isPlatformAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const [loadingLabel, setLoadingLabel] = useState('');

  const deleteConfirmReady = !!deleteTarget && deleteConfirmText === deleteTarget.name;

  // Sync map view from loaded project data to mapStore + fly if map exists
  const syncMapView = (data: Record<string, any>) => {
    if (data.mapCenter || data.mapZoom) {
      const ms = useMapStore.getState();
      ms.setView({
        center: data.mapCenter,
        zoom: data.mapZoom,
        bearing: data.mapBearing,
        pitch: data.mapPitch,
      });
      // If map is already mounted, fly to the saved position
      if (ms.map) {
        ms.map.flyTo({
          center: data.mapCenter,
          zoom: data.mapZoom,
          bearing: data.mapBearing ?? 0,
          pitch: data.mapPitch ?? 0,
          duration: 1500,
        });
      }
    }
  };

  // Fetch projects on open. M-05C: when a platform admin enables "Show archived",
  // request includeArchived=true (the backend honors it for admins only).
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listProjects(
        1,
        100,
        search || undefined,
        isPlatformAdmin && showArchived ? { includeArchived: true } : undefined
      );
      setProjects(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, isPlatformAdmin, showArchived]);

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      listClients()
        .then((c) => setClients(Array.isArray(c) ? c : []))
        .catch(() => {}); // silently fail for non-admin users
    }
  }, [isOpen, fetchProjects]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(fetchProjects, 300);
    return () => clearTimeout(timer);
  }, [search, isOpen, fetchProjects]);

  // Open existing project — delegates to store-owned loader (handles version tracking)
  const handleOpenProject = async (site: SiteListItem) => {
    setLoadingProject(site.id);
    setLoadingLabel(`Downloading "${site.name}"...`);
    setError(null);
    console.log(`[OpenProject] Fetching "${site.name}" (${site.id})...`);
    try {
      const projectData = await loadProjectFromServer(site.id, site.name, site.client_name);
      console.log(`[OpenProject] Loaded (${JSON.stringify(projectData).length} bytes)`);

      syncMapView(projectData as Record<string, any>);

      // Warn if project has no meaningful data
      const isEmpty = !projectData.indications?.length && !projectData.overlays?.length && !projectData.orthophotos?.length;
      if (isEmpty) {
        addNotification({ type: 'info', title: `"${site.name}" is empty`, message: 'No data has been saved to this project yet' });
      } else {
        addNotification({ type: 'success', title: `Opened "${site.name}"` });
      }
      onClose();
    } catch (err) {
      console.error('[OpenProject] Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to open project');
    } finally {
      setLoadingProject(null);
      setLoadingLabel('');
    }
  };

  // Create new project
  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;

    setCreating(true);
    setError(null);
    try {
      const site = await createProject(newName.trim(), newClientId || clients[0]?.id || undefined);

      // Load empty project with the new siteId (also resets map view + IV state)
      loadProject({} as any, {
        siteId: site.id,
        siteName: site.name,
      });

      addNotification({ type: 'success', title: `Created "${site.name}"` });
      setShowNewForm(false);
      setNewName('');
      setNewClientId('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  // Import from file
  const handleImportFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.zip';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      if (file.name.endsWith('.json')) {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          loadProject(data, {
            siteName: file.name.replace(/\.json$/, ''),
          });
          syncMapView(data);
          addNotification({ type: 'success', title: 'Project imported from JSON' });
          onClose();
        } catch (_err) {
          addNotification({ type: 'error', title: 'Invalid JSON file' });
        }
      } else if (file.name.endsWith('.zip')) {
        try {
          addNotification({ type: 'info', title: 'Importing ZIP...', message: 'Reading backup file' });
          const store = useProjectStore.getState();
          const siteId = store.meta.siteId;
          // Don't pass siteId — skip R2 uploads for fast import (blob URLs).
          // Assets will be uploaded to R2 when user clicks "Save to Server" via syncProjectToServer.
          const result = await importProjectZip(file, undefined, (msg) => console.log('[Import]', msg));

          // Check if current project has data worth preserving
          const currentData = store.data;
          const hasExistingData = !!siteId
            || currentData.indications.length > 0
            || currentData.overlays.length > 0
            || currentData.orthophotos.length > 0
            || currentData.flightPaths.length > 0
            || currentData.geoPhotos.length > 0
            || currentData.videoStrips.length > 0
            || (currentData.sldDiagrams && currentData.sldDiagrams.length > 0);

          if (hasExistingData) {
            onClose();
            // Show Replace/Merge dialog (after closing modal so it's not behind)
            const { mergeProjectData } = await import('../../services/projectMerge');
            showConfirm({
              title: 'Import ZIP Backup',
              message: 'This project already has data. How would you like to import the backup?\n\n• Replace All — clear current data, load backup\n• Merge — restore backup data and keep new additions',
              confirmLabel: 'Merge',
              cancelLabel: 'Replace All',
              variant: 'default',
              onConfirm: () => {
                const s = useProjectStore.getState();
                const merged = mergeProjectData(s.data, result.data as any);
                loadProject(merged as any, { siteId: s.meta.siteId, siteName: s.meta.siteName });
                syncMapView(result.data as Record<string, any>);
                addNotification({ type: 'success', title: 'ZIP merged', message: 'Backup restored, new additions kept' });
              },
              onCancel: () => {
                const s = useProjectStore.getState();
                const importMeta = s.meta.siteId
                  ? { siteId: s.meta.siteId, siteName: s.meta.siteName || result.meta.siteName }
                  : result.meta;
                loadProject(result.data as any, importMeta);
                syncMapView(result.data as Record<string, any>);
                addNotification({ type: 'success', title: 'Project replaced from ZIP' });
              },
            });
          } else {
            loadProject(result.data as any, result.meta);
            syncMapView(result.data as Record<string, any>);
            addNotification({ type: 'success', title: 'Project imported from ZIP' });
            onClose();
          }
        } catch (err) {
          addNotification({ type: 'error', title: 'ZIP import failed', message: err instanceof Error ? err.message : 'Unknown error' });
        }
      }
    };
    input.click();
  };

  // Delete project — open the typed-confirmation panel (admin only).
  const requestDeleteProject = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation(); // Don't trigger row click (open)
    setError(null);
    setDeleteTarget(site);
    setDeleteConfirmText('');
  };

  const cancelDeleteProject = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  // Perform the delete only once the typed name matches exactly. Preserves the
  // existing deleteProject(siteId) call shape and post-delete behavior.
  const confirmDeleteProject = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.name) return;
    const site = deleteTarget;
    setDeleting(true);
    setError(null);
    try {
      await deleteProject(site.id);
      addNotification({ type: 'success', title: `Archived "${site.name}"` });
      // If the archived project is currently loaded, clear the store
      if (useProjectStore.getState().meta.siteId === site.id) {
        useProjectStore.getState().loadProject({} as any, {});
      }
      cancelDeleteProject();
      fetchProjects(); // Refresh list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive project');
    } finally {
      setDeleting(false);
    }
  };

  // M-05C: archived-state predicate + restore flow (platform-admin only).
  const isArchived = (site: SiteListItem) => !!site.deleted_at || site.status === 'archived';

  // Open the restore confirmation. Restore is constructive (non-danger). The
  // copy is explicit that share/upload/SCADA stay disabled after restore.
  const requestRestoreProject = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation(); // don't trigger row open
    setError(null);
    showConfirm({
      title: 'Restore this project?',
      message:
        'It will return to your normal project lists. Note: share links remain disabled/revoked, upload links remain disabled, and monitoring (SCADA) credentials remain disabled — you may need to recreate or re-enable those integrations.',
      confirmLabel: 'Restore',
      cancelLabel: 'Cancel',
      variant: 'default',
      onConfirm: () => { void performRestoreProject(site); },
    });
  };

  // An already-active site returns restored:false (idempotent) — still a success;
  // we refresh either way. 403/404 surface safe messages without leaking data.
  const performRestoreProject = async (site: SiteListItem) => {
    setError(null);
    try {
      await restoreProject(site.id);
      addNotification({ type: 'success', title: `Restored "${site.name}"` });
      fetchProjects(); // refresh — restored project rejoins the active list
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        setError('You do not have permission to restore this project.');
      } else if (status === 404) {
        // Inline error only — do NOT refresh here: fetchProjects() begins by
        // clearing the error, which would wipe this message before it renders.
        setError('That project no longer exists.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to restore project');
      }
    }
  };

  // M-05D-3: open the purge panel for an archived row and load the counts-only
  // preview. Purge is permanent/irreversible — separate from Restore.
  const requestPurgeProject = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation(); // don't trigger row open
    setError(null);
    setPurgeTarget(site);
    setPurgeConfirmText('');
    setPurgeReasonCode('');
    setPurgeStatus(null);
    setPurgeError(null);
    setPurgePreview(null);
    setPurgePreviewLoading(true);
    purgePreviewProject(site.id)
      .then((p) => setPurgePreview(p))
      .catch(() => setPurgePreview(null)) // preview is advisory; server re-checks before erasing
      .finally(() => setPurgePreviewLoading(false));
  };

  const cancelPurgeProject = () => {
    setPurgeTarget(null);
    setPurgePreview(null);
    setPurgePreviewLoading(false);
    setPurgeConfirmText('');
    setPurgeReasonCode('');
    setPurgeStatus(null);
    setPurgeError(null);
  };

  // Execute (or retry) the purge once the exact name is typed and a reason is
  // chosen. Completed → drop the row. partial/failed → keep row, show retry.
  const confirmPurgeProject = async () => {
    if (!purgeTarget || purgeConfirmText !== purgeTarget.name || !purgeReasonCode) return;
    const site = purgeTarget;
    setPurging(true);
    setPurgeError(null);
    try {
      const res = await purgeProject(site.id, { confirmName: purgeConfirmText, reasonCode: purgeReasonCode });
      if (res.status === 'completed') {
        addNotification({ type: 'success', title: `Permanently erased "${site.name}"` });
        if (useProjectStore.getState().meta.siteId === site.id) {
          useProjectStore.getState().loadProject({} as any, {});
        }
        cancelPurgeProject();
        fetchProjects(); // refresh — purged project is gone from the list
      } else {
        // partial / failed / still-in-progress: keep the panel + row, allow retry.
        setPurgeStatus(res.status);
        setPurgeError(
          res.status === 'partial' || res.status === 'failed'
            ? 'Purge did not fully complete — some files could not be removed. The project is still archived; you can retry.'
            : 'Purge is in progress. Use “Check status”, or retry to continue.'
        );
      }
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        setPurgeError('You do not have permission to purge this project.');
      } else if (status === 404) {
        setPurgeError('That project no longer exists.');
      } else if (status === 409) {
        setPurgeError('This project is not archived, or a purge is already in progress.');
      } else if (status === 400) {
        setPurgeError('Please type the exact project name and choose a reason.');
      } else {
        setPurgeError(err instanceof Error ? err.message : 'Failed to purge project.');
      }
    } finally {
      setPurging(false);
    }
  };

  // Re-check a still-in-progress purge job (no auto-retry loop).
  const checkPurgeStatus = async (site: SiteListItem) => {
    setPurgeError(null);
    try {
      const s = await getPurgeStatus(site.id);
      setPurgeStatus(s.status);
      if (s.status === 'completed') {
        addNotification({ type: 'success', title: `Permanently erased "${site.name}"` });
        cancelPurgeProject();
        fetchProjects();
      } else if (s.status === 'partial' || s.status === 'failed') {
        setPurgeError('Purge did not fully complete. The project is still archived; you can retry.');
      }
    } catch {
      setPurgeError('Could not check purge status. You can retry.');
    }
  };

  // Move to another organization — open the panel for an active row and load
  // the org list on first use. Links keep working for the new owner (owner
  // ruling); members of the old org lose access on their next request.
  const requestMoveProject = (e: React.MouseEvent, site: SiteListItem) => {
    e.stopPropagation(); // don't trigger row open
    setError(null);
    setMoveTarget(site);
    setMoveOrgId('');
    setMoveConfirmText('');
    setMoveError(null);
    if (adminOrgs === null) {
      listAdminOrganizations()
        .then((orgs) => setAdminOrgs(orgs))
        .catch(() => {
          setAdminOrgs([]);
          setMoveError('Could not load organizations.');
        });
    }
  };

  const cancelMoveProject = () => {
    setMoveTarget(null);
    setMoveOrgId('');
    setMoveConfirmText('');
    setMoveError(null);
  };

  const orgNameOf = (id?: string | null): string | null =>
    adminOrgs?.find((o) => o.id === id)?.name ?? null;

  const confirmMoveProject = async () => {
    if (!moveTarget || !moveOrgId || moveConfirmText !== moveTarget.name) return;
    const site = moveTarget;
    const targetName = orgNameOf(moveOrgId) ?? 'the selected organization';
    setMoving(true);
    setMoveError(null);
    try {
      const res = await moveProject(site.id, { targetOrgId: moveOrgId, confirmName: moveConfirmText });
      addNotification({
        type: 'success',
        title: res.moved ? `Moved "${site.name}" to ${targetName}` : `"${site.name}" already belongs to ${targetName}`,
      });
      cancelMoveProject();
      fetchProjects(); // client_name / org changed
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        setMoveError('You do not have permission to move this project.');
      } else if (status === 404) {
        setMoveError('That project or organization no longer exists.');
      } else if (status === 409) {
        setMoveError('This project is archived or changed meanwhile. Restore it first, or reload and retry.');
      } else if (status === 400) {
        setMoveError('Please type the exact project name and choose an organization.');
      } else {
        setMoveError(err instanceof Error ? err.message : 'Failed to move project.');
      }
    } finally {
      setMoving(false);
    }
  };

  // Build a safe counts-only list from a purge preview (counts/categories only;
  // null categories are omitted; no raw keys are ever present in the payload).
  const renderPurgeCounts = (preview: PurgePreviewResult) => {
    const entries: [string, number][] = [];
    for (const [k, v] of Object.entries(preview.counts?.d1 ?? {})) {
      if (typeof v === 'number') entries.push([k, v]);
    }
    for (const [k, v] of Object.entries(preview.counts?.r2 ?? {})) {
      if (typeof v === 'number') entries.push([`r2 · ${k}`, v]);
    }
    if (entries.length === 0) {
      return <li className="project-select__purge-count">No counted records.</li>;
    }
    return entries.map(([k, v]) => (
      <li key={k} className="project-select__purge-count">
        <span className="project-select__purge-count-label">{k}</span>
        <span className="project-select__purge-count-value">{v}</span>
      </li>
    ));
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // M-05C: active rows first, archived rows grouped after. Array.sort is stable,
  // so the backend's updated_at DESC order is preserved within each group.
  const orderedProjects = [...projects].sort(
    (a, b) => (isArchived(a) ? 1 : 0) - (isArchived(b) ? 1 : 0)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Project"
      size="lg"
      closeOnBackdrop={false}
    >
      <div className="project-select">
        {/* Search + Actions bar */}
        <div className="project-select__toolbar">
          <div className="project-select__search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="project-select__search-icon">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="project-select__search-input"
            />
          </div>
          {/* M-05C: platform-admin-only archived filter; non-admins never see it. */}
          {isPlatformAdmin && (
            <label className="project-select__archived-toggle">
              <input
                type="checkbox"
                data-testid="show-archived-toggle"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              <span>Show archived</span>
            </label>
          )}
          <div className="project-select__actions">
            <Button variant="primary" size="sm" onClick={() => setShowNewForm(true)}>
              New Project
            </Button>
            <Button variant="ghost" size="sm" onClick={handleImportFile}>
              Import File
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="project-select__error">{error}</div>
        )}

        {/* Archive confirmation — typed exact-name, admin only (M-05A guardrails;
            M-05B archive/recoverable semantics). The backend route stays
            DELETE /sites/:id but now soft-archives rather than hard-deletes. */}
        {deleteTarget && (
          <div className="project-select__delete-confirm" role="alertdialog" aria-label="Confirm project archive">
            <div className="project-select__delete-confirm-title">
              Archive “{deleteTarget.name}”
            </div>
            <p className="project-select__delete-confirm-warning">
              This archives the project and hides it from normal users and project lists. Public sharing, upload links, and monitoring access will be disabled.
            </p>
            <p className="project-select__delete-confirm-label">
              A platform admin can restore it later — but public/share links and integrations may need to be recreated or re-enabled. The project's data is not erased.
            </p>
            <label className="project-select__delete-confirm-label" htmlFor="project-delete-input">
              Type the exact project name to confirm:
            </label>
            <input
              id="project-delete-input"
              data-testid="project-delete-input"
              type="text"
              className="project-select__input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteTarget.name}
              autoComplete="off"
              autoFocus
            />
            <div className="project-select__delete-confirm-actions">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="project-delete-cancel"
                onClick={cancelDeleteProject}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                type="button"
                data-testid="project-delete-confirm"
                disabled={!deleteConfirmReady || deleting}
                loading={deleting}
                onClick={confirmDeleteProject}
              >
                Archive project
              </Button>
            </div>
          </div>
        )}

        {/* M-05D-3: physical purge (true erasure) — admin-only, archived rows
            only, IRREVERSIBLE. Distinct from Restore; starts with a counts-only
            preview, then requires the exact name + a reason code. */}
        {purgeTarget && (
          <div className="project-select__purge-confirm" role="alertdialog" aria-label="Confirm permanent project purge">
            <div className="project-select__purge-confirm-title">
              Permanently erase “{purgeTarget.name}”
            </div>
            <p className="project-select__purge-confirm-warning">
              This permanently and irreversibly erases the project, all its data, and all stored files. It CANNOT be restored. Archiving is recoverable — purging is not. Use this only for true erasure (e.g. customer offboarding or a data-removal request).
            </p>
            <p className="project-select__delete-confirm-label">
              Review what will be permanently erased before continuing:
            </p>
            {purgePreviewLoading ? (
              <div className="project-select__purge-preview">
                <span className="project-select__spinner" /> Loading preview…
              </div>
            ) : purgePreview ? (
              <ul className="project-select__purge-counts" data-testid="project-purge-preview">
                {renderPurgeCounts(purgePreview)}
              </ul>
            ) : (
              <div className="project-select__purge-preview project-select__purge-preview--error" data-testid="project-purge-preview-error">
                Could not load the purge preview. You may still proceed — the server re-checks before erasing.
              </div>
            )}
            <label className="project-select__delete-confirm-label" htmlFor="project-purge-reason">
              Reason for permanent erasure:
            </label>
            <select
              id="project-purge-reason"
              data-testid="project-purge-reason"
              className="project-select__select"
              value={purgeReasonCode}
              onChange={(e) => setPurgeReasonCode(e.target.value)}
            >
              <option value="">Select a reason…</option>
              {PURGE_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <label className="project-select__delete-confirm-label" htmlFor="project-purge-input">
              Type the exact project name to confirm permanent erasure:
            </label>
            <input
              id="project-purge-input"
              data-testid="project-purge-input"
              type="text"
              className="project-select__input"
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder={purgeTarget.name}
              autoComplete="off"
            />
            {purgeError && (
              <p className="project-select__purge-confirm-warning" data-testid="project-purge-error">{purgeError}</p>
            )}
            {(purgeStatus === 'started' || purgeStatus === 'r2_deleting' || purgeStatus === 'd1_deleting') && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="project-purge-status-refresh"
                onClick={() => checkPurgeStatus(purgeTarget)}
              >
                Check status
              </Button>
            )}
            <div className="project-select__delete-confirm-actions">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="project-purge-cancel"
                onClick={cancelPurgeProject}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                type="button"
                data-testid="project-purge-confirm"
                disabled={purgeConfirmText !== purgeTarget.name || !purgeReasonCode || purging}
                loading={purging}
                onClick={confirmPurgeProject}
              >
                {purgeStatus === 'partial' || purgeStatus === 'failed' ? 'Retry purge' : 'Purge permanently'}
              </Button>
            </div>
          </div>
        )}

        {moveTarget && (
          <div className="project-select__delete-confirm project-select__move-confirm" role="dialog" aria-label="Move project to another organization">
            <div className="project-select__delete-confirm-title">
              Move “{moveTarget.name}” to another organization
            </div>
            <p className="project-select__delete-confirm-label">
              Current organization: {orgNameOf(moveTarget.org_id) ?? moveTarget.client_name ?? 'unknown'}.
              Share links and field-upload links keep working for the new owner. Members of the current organization lose access immediately.
            </p>
            <label className="project-select__delete-confirm-label" htmlFor="project-move-org">
              Move to:
            </label>
            <select
              id="project-move-org"
              data-testid="project-move-org"
              className="project-select__select"
              value={moveOrgId}
              onChange={(e) => setMoveOrgId(e.target.value)}
              disabled={adminOrgs === null}
            >
              <option value="">{adminOrgs === null ? 'Loading organizations…' : 'Select an organization…'}</option>
              {(adminOrgs ?? [])
                .filter((o) => o.status !== 'deleted' && o.id !== moveTarget.org_id)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}{o.plan_id ? ` · ${o.plan_id}` : ''} · {o.site_count} {o.site_count === 1 ? 'site' : 'sites'}
                  </option>
                ))}
            </select>
            <label className="project-select__delete-confirm-label" htmlFor="project-move-input">
              Type the exact project name to confirm the move:
            </label>
            <input
              id="project-move-input"
              data-testid="project-move-input"
              type="text"
              className="project-select__input"
              value={moveConfirmText}
              onChange={(e) => setMoveConfirmText(e.target.value)}
              placeholder={moveTarget.name}
              autoComplete="off"
            />
            {moveError && (
              <p className="project-select__delete-confirm-warning" data-testid="project-move-error">{moveError}</p>
            )}
            <div className="project-select__delete-confirm-actions">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="project-move-cancel"
                onClick={cancelMoveProject}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="button"
                data-testid="project-move-confirm"
                disabled={!moveOrgId || moveConfirmText !== moveTarget.name || moving}
                loading={moving}
                onClick={confirmMoveProject}
              >
                Move project
              </Button>
            </div>
          </div>
        )}

        {/* New project form */}
        {showNewForm && (
          <form className="project-select__new-form" onSubmit={handleCreateProject}>
            <div className="project-select__new-form-row">
              <input
                type="text"
                placeholder="Project name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="project-select__input"
                autoFocus
              />
              {clients.length > 0 && (
                <select
                  value={newClientId}
                  onChange={(e) => setNewClientId(e.target.value)}
                  className="project-select__select"
                >
                  <option value="">Select client...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="project-select__new-form-actions">
              <Button variant="primary" size="sm" type="submit" loading={creating} disabled={!newName.trim()}>
                Create
              </Button>
              <Button variant="ghost" size="sm" type="button" onClick={() => { setShowNewForm(false); setNewName(''); setError(null); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Project list */}
        <div className="project-select__list">
          {loadingProject ? (
            <div className="project-select__loading-card">
              <span className="project-select__spinner project-select__spinner--lg" />
              <span className="project-select__loading-label">{loadingLabel || 'Loading...'}</span>
            </div>
          ) : loading && projects.length === 0 ? (
            <div className="project-select__empty">Loading projects...</div>
          ) : projects.length === 0 ? (
            <div className="project-select__empty">
              {search ? 'No projects match your search.' : 'No projects yet. Create your first project.'}
            </div>
          ) : (
            orderedProjects.map((site) => {
              const archived = isArchived(site);
              return (
                <div
                  key={site.id}
                  data-testid={`project-row-${site.id}`}
                  className={`project-select__item${loadingProject === site.id ? ' project-select__item--disabled' : ''}${archived ? ' project-select__item--archived' : ''}`}
                  onClick={archived ? undefined : () => { if (!loadingProject) handleOpenProject(site); }}
                  onKeyDown={archived ? undefined : (e) => { if (e.key === 'Enter' && !loadingProject) handleOpenProject(site); }}
                  role={archived ? undefined : 'button'}
                  tabIndex={archived ? undefined : 0}
                  aria-disabled={archived || undefined}
                >
                  <div className="project-select__item-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <div className="project-select__item-info">
                    <div className="project-select__item-name">
                      {site.name}
                      {archived && (
                        <span
                          className="project-select__badge project-select__badge--archived"
                          data-testid="project-archived-badge"
                        >
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="project-select__item-meta">
                      {site.client_name && <span>{site.client_name}</span>}
                      {site.location && <span>{site.location}</span>}
                      {archived ? (
                        <span>{site.deleted_at ? `Archived ${formatDate(site.deleted_at)}` : 'Archived'}</span>
                      ) : (
                        <span>{formatDate(site.updated_at)}</span>
                      )}
                    </div>
                  </div>
                  {/* Move to another organization — platform admin, active rows only. */}
                  {isPlatformAdmin && !archived && (
                    <button
                      type="button"
                      className="project-select__item-move"
                      data-testid={`project-move-${site.id}`}
                      title="Move project to another organization"
                      onClick={(e) => requestMoveProject(e, site)}
                    >
                      Move…
                    </button>
                  )}
                  {/* M-05A archive affordance — active rows only. */}
                  {isPlatformAdmin && !archived && (
                    <button
                      type="button"
                      className="project-select__item-delete"
                      title="Archive project"
                      onClick={(e) => requestDeleteProject(e, site)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/>
                        <path d="M14 11v6"/>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  )}
                  {/* M-05C restore + M-05D-3 purge — archived rows, platform-admin
                      only. Restore is primary/safe; Purge is a danger-styled
                      secondary action for permanent, irreversible erasure. */}
                  {isPlatformAdmin && archived && (
                    <div className="project-select__archived-actions">
                      <button
                        type="button"
                        className="project-select__item-restore"
                        data-testid={`project-restore-${site.id}`}
                        onClick={(e) => requestRestoreProject(e, site)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="project-select__item-purge"
                        data-testid={`project-purge-${site.id}`}
                        title="Permanently erase project"
                        onClick={(e) => requestPurgeProject(e, site)}
                      >
                        Purge…
                      </button>
                    </div>
                  )}
                  {!archived && (
                    <div className="project-select__item-action">
                      {loadingProject === site.id ? (
                        <span className="project-select__spinner" />
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
